import { LogIn, ShieldCheck, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

function getMode() {
  return window.location.pathname.includes('/accounts/register/') ? 'register' : 'login';
}

function nextFromQuery() {
  const params = new URLSearchParams(window.location.search);
  return params.get('next') || '';
}

function referralCodeFromQuery() {
  const params = new URLSearchParams(window.location.search);
  return params.get('referral_code') || '';
}

async function requestJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRFToken': getCookie('csrftoken') || '',
    },
    body: JSON.stringify(body),
  });

  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok || !payload.success) {
    const error = new Error(payload.error || 'Ошибка запроса');
    error.payload = payload;
    throw error;
  }

  return payload;
}

export default function App() {
  const [mode, setMode] = useState(getMode());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
  });

  const [registerForm, setRegisterForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    phone: '',
    password1: '',
    password2: '',
    referral_code: referralCodeFromQuery(),
  });

  const [fieldErrors, setFieldErrors] = useState({});

  const nextTarget = useMemo(() => nextFromQuery(), []);

  const buildModeUrl = (nextMode) => {
    const query = new URLSearchParams();
    const referralCode = (registerForm.referral_code || '').trim();

    if (nextTarget) {
      query.set('next', nextTarget);
    }

    if (referralCode) {
      query.set('referral_code', referralCode);
    }

    const queryString = query.toString();
    const basePath = nextMode === 'register' ? '/accounts/register/' : '/accounts/login/';

    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  const goToMode = (nextMode) => {
    const target = buildModeUrl(nextMode);
    window.history.replaceState({}, '', target);
    setMode(nextMode);
    setError('');
    setFieldErrors({});
  };

  const submitLogin = async (event) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError('');
    try {
      const payload = await requestJson('/accounts/api/login/', {
        username: loginForm.username,
        password: loginForm.password,
        next: nextTarget,
      });
      window.location.assign(payload.redirect_url || '/clients/app/');
    } catch (err) {
      setError(err.message || 'Не удалось выполнить вход');
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (event) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError('');
    setFieldErrors({});

    try {
      const payload = await requestJson('/accounts/api/register/', {
        ...registerForm,
        next: nextTarget,
      });
      window.location.assign(payload.redirect_url);
    } catch (err) {
      setError(err.message || 'Не удалось начать регистрацию');
      setFieldErrors(err.payload?.field_errors || {});
    } finally {
      setBusy(false);
    }
  };

  const renderFieldError = (field) => {
    const errors = fieldErrors?.[field];
    if (!errors || !errors.length) return null;
    return <p className="mt-1 text-xs font-semibold text-rose-600">{errors[0]}</p>;
  };

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <aside className="auth-brand">
          <img src="/static/logo_big.svg" alt="ОрбитаПарк" className="auth-logo" />
          <h1>ОрбитаПарк</h1>
          <p>Вход и регистрация в системе ОрбитаПарк.</p>
        </aside>

        <section className="auth-card">
          <div className="auth-tabs">
            <button type="button" className={mode === 'login' ? 'active' : ''} onClick={() => goToMode('login')}>
              <LogIn className="h-4 w-4" /> Вход
            </button>
            <button type="button" className={mode === 'register' ? 'active' : ''} onClick={() => goToMode('register')}>
              <UserPlus className="h-4 w-4" /> Регистрация
            </button>
          </div>

          {error ? <div className="auth-error">{error}</div> : null}

          {mode === 'login' ? (
            <form onSubmit={submitLogin} className="auth-form">
              <label>
                Логин или телефон
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(event) => setLoginForm((prev) => ({ ...prev, username: event.target.value }))}
                  autoComplete="username"
                  required
                />
              </label>

              <label>
                Пароль
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(event) => setLoginForm((prev) => ({ ...prev, password: event.target.value }))}
                  autoComplete="current-password"
                  required
                />
              </label>

              <button type="submit" disabled={busy} className="auth-submit">
                {busy ? 'Входим...' : 'Войти'}
              </button>
            </form>
          ) : (
            <form onSubmit={submitRegister} className="auth-form">
              <label>
                Логин
                <input
                  type="text"
                  value={registerForm.username}
                  onChange={(event) => setRegisterForm((prev) => ({ ...prev, username: event.target.value }))}
                  autoComplete="username"
                  required
                />
                {renderFieldError('username')}
              </label>

              <div className="auth-grid">
                <label>
                  Имя
                  <input
                    type="text"
                    value={registerForm.first_name}
                    onChange={(event) => setRegisterForm((prev) => ({ ...prev, first_name: event.target.value }))}
                    required
                  />
                  {renderFieldError('first_name')}
                </label>

                <label>
                  Фамилия
                  <input
                    type="text"
                    value={registerForm.last_name}
                    onChange={(event) => setRegisterForm((prev) => ({ ...prev, last_name: event.target.value }))}
                    required
                  />
                  {renderFieldError('last_name')}
                </label>
              </div>

              <label>
                Телефон
                <input
                  type="text"
                  value={registerForm.phone}
                  onChange={(event) => setRegisterForm((prev) => ({ ...prev, phone: event.target.value }))}
                  placeholder="+7 (999) 999-99-99"
                  required
                />
                {renderFieldError('phone')}
              </label>

              <div className="auth-grid">
                <label>
                  Пароль
                  <input
                    type="password"
                    value={registerForm.password1}
                    onChange={(event) => setRegisterForm((prev) => ({ ...prev, password1: event.target.value }))}
                    autoComplete="new-password"
                    required
                  />
                  {renderFieldError('password1')}
                </label>

                <label>
                  Повторите пароль
                  <input
                    type="password"
                    value={registerForm.password2}
                    onChange={(event) => setRegisterForm((prev) => ({ ...prev, password2: event.target.value }))}
                    autoComplete="new-password"
                    required
                  />
                  {renderFieldError('password2')}
                </label>
              </div>

              <label>
                Реферальный код (необязательно)
                <input
                  type="text"
                  value={registerForm.referral_code}
                  onChange={(event) => setRegisterForm((prev) => ({ ...prev, referral_code: event.target.value }))}
                  maxLength={6}
                />
              </label>

              <div className="agreement-note">
                <ShieldCheck className="h-4 w-4" />
                <div className="agreement-note__content">
                  Нажимая «Зарегистрироваться», вы подтверждаете согласие с
                  {' '}
                  <a href="/terms/" target="_blank" rel="noreferrer">пользовательским соглашением</a>
                  {' '}
                  и
                  {' '}
                  <a href="/privacy/" target="_blank" rel="noreferrer">политикой конфиденциальности</a>.
                </div>
              </div>

              <button type="submit" disabled={busy} className="auth-submit">
                {busy ? 'Создаем...' : 'Зарегистрироваться'}
              </button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
